import { useState, useRef } from "react";
import {
    Box,
    TableSortLabel,
    IconButton,
    Popper,
    Paper,
    TextField,
    ClickAwayListener,
    Button
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";

const SortableFilterableHeaderCell = ({
    label,
    columnKey,
    sortConfig,
    onSortChange,
    filterValue,
    onFilterChange,
    disableFilter = false,
    hasCheckBox = false
}) => {
    const isActive = sortConfig?.key === columnKey;
    const anchorRef = useRef(null);
    const [openFilter, setOpenFilter] = useState(false);
    const [tempFilter, setTempFilter] = useState(filterValue || "");

    const handleSortClick = () => {
        const newDirection =
            isActive && sortConfig.direction === "asc" ? "desc" : "asc";
        onSortChange({ key: columnKey, direction: newDirection });
    };

    const handleFilterIconClick = () => {
        setTempFilter(filterValue || ""); // Reset temp filter
        setOpenFilter((prev) => !prev);
    };

    const handleApplyFilter = () => {
        onFilterChange(columnKey, tempFilter);
        setOpenFilter(false);
    };

    return (
        <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{
                paddingLeft: hasCheckBox ? 0 : "30px"
            }}
        >
            {/* Label */}
            <Box
                fontWeight="bold"
                sx={{
                    textTransform: "capitalize",
                    cursor: "pointer"
                }}
                onClick={handleSortClick}
            >
                {label}
            </Box>

            {/* Sort Icon */}
            <TableSortLabel
                active={isActive}
                direction={isActive ? sortConfig.direction : "asc"}
                onClick={handleSortClick}
                sx={{ cursor: "pointer" }}
            />

            {/* Filter Icon */}
            {!disableFilter && (
                <>
                    <IconButton
                        size="small"
                        ref={anchorRef}
                        onClick={handleFilterIconClick}
                        sx={{ padding: 0 }}
                    >
                        <FilterListIcon
                            fontSize="small"
                            sx={{
                                color: filterValue ? "primary.main" : "inherit" // Highlight if active
                            }}
                        />
                    </IconButton>

                    <Popper
                        open={openFilter}
                        anchorEl={anchorRef.current}
                        placement="bottom-start"
                        disablePortal
                        modifiers={[
                            {
                                name: 'preventOverflow',
                                options: {
                                    boundary: 'viewport',
                                    padding: 8
                                }
                            },
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 8]
                                }
                            }
                        ]}
                        style={{ zIndex: 1300 }}
                    >
                        <ClickAwayListener
                            onClickAway={(e) => {
                                if (!anchorRef.current?.contains(e.target)) {
                                    setOpenFilter(false);
                                }
                            }}
                        >
                            <Paper sx={{ p: 1, mt: 0.5, display: 'flex', gap: 1 }}>
                                <TextField
                                    variant="standard"
                                    value={tempFilter}
                                    onChange={(e) => setTempFilter(e.target.value)}
                                    placeholder="Filter..."
                                    size="small"
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleApplyFilter();
                                    }}
                                    autoFocus
                                />
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleApplyFilter}
                                    sx={{ minWidth: '60px', textTransform: 'none' }}
                                >
                                    Search
                                </Button>
                            </Paper>
                        </ClickAwayListener>
                    </Popper>
                </>
            )}
        </Box>
    );
};

export default SortableFilterableHeaderCell;